import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Clock, User, FileText, Paperclip, CheckCircle, AlertCircle, Target, Image as ImageIcon } from 'lucide-react';
import { format } from 'date-fns';
import { getReportById } from "@/api/report";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';

const DetailReport: React.FC = () => {
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');

    const navigate = useNavigate();

    useEffect(() => {
        if (!id) {
            navigate('/report');
        }
    }, [id, navigate]);

    const { data: reportData, isLoading } = useQuery({
        queryKey: ['report', id],
        queryFn: () => getReportById(id as string),
    });

    if (isLoading) {
        return (
            <div className="container mx-auto p-2 sm:p-6 space-y-4 sm:space-y-6">
                <Card>
                    <CardHeader className="space-y-2">
                        <Skeleton className="h-8 w-[200px]" />
                        <Skeleton className="h-4 w-[300px]" />
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                            <Card>
                                <CardHeader className="space-y-2">
                                    <Skeleton className="h-6 w-[150px]" />
                                    <Skeleton className="h-4 w-[200px]" />
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-[80%]" />
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader className="space-y-2">
                                    <Skeleton className="h-6 w-[150px]" />
                                </CardHeader>
                                <CardContent>
                                    <Skeleton className="h-4 w-full" />
                                </CardContent>
                            </Card>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="space-y-2">
                        <Skeleton className="h-8 w-[200px]" />
                        <Skeleton className="h-4 w-[300px]" />
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="space-y-2">
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-[80%]" />
                                    {i < 3 && <Separator className="my-4" />}
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!reportData) {
        return (
            <div className="container mx-auto p-2 sm:p-6">
                <Card>
                    <CardContent className="flex justify-center items-center h-[400px]">
                        <p className="text-muted-foreground text-sm sm:text-base">No report found</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const { report, user, reportDetail } = reportData;

    return (
        <div className="container mx-auto p-2 sm:p-6 space-y-4 sm:space-y-6">
            <Card>
                <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0">
                    <div>
                        <CardTitle className="text-xl sm:text-2xl">Report Details</CardTitle>
                        <CardDescription className="text-sm sm:text-base">Detailed information about the report</CardDescription>
                    </div>
                    <Badge variant="outline" className="text-sm sm:text-base">
                        <Clock className="mr-2 h-4 w-4" />
                        {format(new Date(report.date), 'PPP')}
                    </Badge>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        {/* User Information */}
                        <Card>
                            <CardHeader className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4">
                                <Avatar className="h-12 w-12 sm:h-16 sm:w-16">
                                    <AvatarImage
                                        src={user.avatarUrl || '/default-avatar.png'}
                                        alt={user.name}
                                    />
                                    <AvatarFallback>
                                        {user.name.charAt(0)}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="text-center sm:text-left">
                                    <CardTitle className="text-lg sm:text-xl">{user.name}</CardTitle>
                                    <CardDescription className="text-sm sm:text-base">{user.email}</CardDescription>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    <div className="flex items-center">
                                        <User className="mr-2 h-4 w-4 text-muted-foreground" />
                                        <span className="text-sm sm:text-base">{user.position || 'No position specified'}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
                                        <span className="text-sm sm:text-base">{user.role}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Report Description */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg sm:text-xl">Report Description</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm sm:text-base text-muted-foreground">
                                    {report.description || 'No description provided'}
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </CardContent>
            </Card>

            {/* Report Status Information */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-xl sm:text-2xl">Report Status</CardTitle>
                    <CardDescription className="text-sm sm:text-base">Current status and progress information</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                        {/* Delivered */}
                        <Card>
                            <CardHeader className="pb-2">
                                <div className="flex items-center space-x-2">
                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                    <CardTitle className="text-sm sm:text-base">Delivered</CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm sm:text-base text-muted-foreground">
                                    {report.delivered || 'No delivered items'}
                                </p>
                            </CardContent>
                        </Card>

                        {/* In Progress */}
                        <Card>
                            <CardHeader className="pb-2">
                                <div className="flex items-center space-x-2">
                                    <Clock className="h-5 w-5 text-blue-600" />
                                    <CardTitle className="text-sm sm:text-base">In Progress</CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm sm:text-base text-muted-foreground">
                                    {report.inProgress || 'No items in progress'}
                                </p>
                            </CardContent>
                        </Card>

                        {/* Blockers */}
                        <Card>
                            <CardHeader className="pb-2">
                                <div className="flex items-center space-x-2">
                                    <AlertCircle className="h-5 w-5 text-red-600" />
                                    <CardTitle className="text-sm sm:text-base">Blockers</CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm sm:text-base text-muted-foreground">
                                    {report.blockers || 'No blockers identified'}
                                </p>
                            </CardContent>
                        </Card>

                        {/* Value Delivered */}
                        <Card>
                            <CardHeader className="pb-2">
                                <div className="flex items-center space-x-2">
                                    <Target className="h-5 w-5 text-purple-600" />
                                    <CardTitle className="text-sm sm:text-base">Value Delivered</CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm sm:text-base text-muted-foreground">
                                    {report.valueDelivered || 'No value delivered specified'}
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </CardContent>
            </Card>

            {/* Report Image */}
            {report.imageUrl && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-xl sm:text-2xl flex items-center">
                            <ImageIcon className="mr-2 h-6 w-6" />
                            Report Image
                        </CardTitle>
                        <CardDescription className="text-sm sm:text-base">Attached image for this report</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex justify-center">
                            <img
                                src={report.imageUrl}
                                alt="Report attachment"
                                className="max-w-full h-auto rounded-lg shadow-md max-h-[400px] object-contain"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                }}
                            />
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Report Details */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-xl sm:text-2xl">Report Tasks</CardTitle>
                    <CardDescription className="text-sm sm:text-base">Detailed breakdown of report entries</CardDescription>
                </CardHeader>
                <CardContent>
                    <ScrollArea className="h-[300px] sm:h-[400px] w-full rounded-md border p-2 sm:p-4">
                        {reportDetail.length > 0 ? (
                            reportDetail.map((detail, index) => (
                                <React.Fragment key={detail.detailReport._id}>
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
                                        <div className="flex items-center space-x-4">
                                            <Paperclip className="h-5 w-5 text-muted-foreground" />
                                            <div>
                                                <p className="text-sm font-medium">{detail.task.name}</p>
                                                <p className="text-xs text-muted-foreground">{detail.detailReport.note}</p>
                                            </div>
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {format(new Date(detail.detailReport.createdAt), 'PPp')}
                                        </div>
                                    </div>
                                    {index < reportDetail.length - 1 && <Separator className="my-4" />}
                                </React.Fragment>
                            ))
                        ) : (
                            <p className="text-center text-sm sm:text-base text-muted-foreground">No task details found</p>
                        )}
                    </ScrollArea>
                </CardContent>
            </Card>
        </div>
    );
};

export default DetailReport;